// ============================================================================
// solver.ts — solveur de contraintes géométriques par RELAXATION (position-based).
// Pur, déterministe, SANS librairie externe. Gauss-Seidel : à chaque itération,
// chaque contrainte projette les points MOBILES (non `fixed`, non `pinned`) pour
// réduire son erreur. Best-effort si le système est sur-contraint.
//
// Sprint VCE-1 — 3 améliorations (cf. docs/visual-constraint-engine.md §5) :
//   1. Index `Map<id, CGPoint>` → supprime le `points.find` O(n) de la boucle chaude.
//   2. Warm-start (`initialPos`) → repartir des positions de la frame précédente
//      (drag/slider fluides, convergence en moins d'itérations).
//   3. Résidu retourné (`solveDetailed`) → erreur par contrainte, pour détecter
//      les sur-contraintes (UX « contrainte rouge ») et la non-convergence.
//
// `solve()` reste rétro-compatible (renvoie `CGPoint[]`). `solveDetailed()` expose
// positions + résidus + statut.
// ============================================================================

import type { CGConstraint, CGPoint } from '@jeraisonne/ai/visual-blocks';
import { evalCGValue, type Scope } from './expr';

const EPS = 1e-9;
/** Tolérances de violation (résidu). Longueur en unités math, angle en radians. */
const TOL_LEN = 1e-3;
const TOL_ANG = 1e-3; // ≈ 0,057°
/**
 * Borne dure des coordonnées (filet de sécurité anti-divergence). Une figure
 * pédagogique vit dans ±quelques dizaines ; au-delà de ±1e5 c'est une
 * divergence (sur-contrainte dégénérée, division par ~0) → on clampe au lieu
 * de propager des coordonnées astronomiques (e+262).
 */
const COORD_CAP = 1e5;

const clampCoord = (v: number, fallback: number): number => {
  const base = Number.isFinite(v) ? v : Number.isFinite(fallback) ? fallback : 0;
  return base > COORD_CAP ? COORD_CAP : base < -COORD_CAP ? -COORD_CAP : base;
};

export interface Vec {
  x: number;
  y: number;
}

export interface SolveOptions {
  /** Point épinglé au curseur pendant un drag : traité comme immobile. */
  pinned?: string | null;
  /** Nombre d'itérations de relaxation (défaut 120 ; ~40 suffisent en warm-start). */
  iterations?: number;
  /**
   * Warm-start : positions de départ des points MOBILES (`id → {x,y}`). Permet de
   * repartir de l'état résolu précédent (drag/slider continus, convergence rapide,
   * pas de saut). Les points `fixed` et `pinned` ignorent ce champ (ils gardent
   * leurs coordonnées d'entrée). Absent ⇒ on part des coordonnées des `points`.
   */
  initialPos?: ReadonlyMap<string, Vec> | Readonly<Record<string, Vec>>;
}

export interface SolveResult {
  /** Points résolus (mêmes champs que l'entrée, `x`/`y` mis à jour). */
  points: CGPoint[];
  /**
   * Positions résolues en `Map` — réutilisable telle quelle comme `initialPos`
   * du prochain `solveDetailed` (warm-start de la frame suivante).
   */
  positions: Map<string, Vec>;
  /**
   * Erreur résiduelle par contrainte après convergence. Unité : longueur (math)
   * pour les contraintes métriques, radians pour les contraintes angulaires.
   */
  residualByConstraint: Record<string, number>;
  /**
   * Ratio de violation maximal (résidu / tolérance, sans dimension). `≤ 1` ⇒
   * toutes les contraintes sont satisfaites. `> 1` ⇒ au moins une est violée.
   */
  residualNorm: number;
  /** `converged` si `residualNorm ≤ 1`, sinon `conflict` (sur-contraint/incompatible). */
  status: 'converged' | 'conflict';
  /** Nombre d'itérations effectuées. */
  iterations: number;
}

function toPosMap(src: SolveOptions['initialPos']): Map<string, Vec> | null {
  if (!src) return null;
  if (src instanceof Map) return src as Map<string, Vec>;
  return new Map(Object.entries(src as Record<string, Vec>));
}

/**
 * Cœur de relaxation. Mute et renvoie la `Map` de positions. Partagé par
 * `solve` et `solveDetailed` (un seul chemin de calcul).
 */
function relax(
  points: CGPoint[],
  constraints: CGConstraint[],
  x: number | Scope,
  options: SolveOptions,
): { pos: Map<string, Vec>; byId: Map<string, CGPoint>; iterations: number } {
  const { pinned = null, iterations = 120 } = options;
  // Une contrainte désactivée (`enabled === false`) est ignorée par le solveur,
  // sans être supprimée du bloc (toggle prof).
  const active = constraints.filter((c) => c.enabled !== false);
  const warm = toPosMap(options.initialPos);
  // Portée de base pour les expressions ; `x` y est surchargé par l'abscisse du
  // point pour les contraintes `on_function` (courbe y=f(x)).
  const baseScope: Scope = typeof x === 'number' ? { x } : x;

  // (1) Index unique des points → fin du `points.find` O(n) dans la boucle chaude.
  const byId = new Map<string, CGPoint>();
  for (const p of points) byId.set(p.id, p);

  const pos = new Map<string, Vec>();
  for (const p of points) {
    // (2) Warm-start : un point mobile part de `initialPos` s'il y figure ;
    //     un point `fixed`/`pinned` part toujours de ses coordonnées d'entrée.
    const mobile = !p.fixed && p.id !== pinned;
    const seed = mobile ? warm?.get(p.id) : undefined;
    pos.set(p.id, seed ? { x: seed.x, y: seed.y } : { x: p.x, y: p.y });
  }

  const movable = (id: string): boolean => {
    const p = byId.get(id);
    return !!p && !p.fixed && id !== pinned;
  };
  const get = (id: string): Vec | null => pos.get(id) ?? null;
  const move = (id: string, dx: number, dy: number) => {
    const p = pos.get(id);
    if (!p) return;
    p.x += dx;
    p.y += dy;
  };
  const rotateAround = (id: string, center: Vec, theta: number) => {
    const p = pos.get(id);
    if (!p) return;
    const dx = p.x - center.x;
    const dy = p.y - center.y;
    const cos = Math.cos(theta);
    const sin = Math.sin(theta);
    p.x = center.x + dx * cos - dy * sin;
    p.y = center.y + dx * sin + dy * cos;
  };
  /** Répartition de la correction entre deux extrémités selon leur mobilité. */
  const weights = (a: string, b: string): [number, number] => {
    const ma = movable(a);
    const mb = movable(b);
    if (ma && mb) return [0.5, 0.5];
    if (ma) return [1, 0];
    if (mb) return [0, 1];
    return [0, 0];
  };

  /** Aligne le segment (id1,id2) sur `targetAngle` (modulo `mod`), au plus court. */
  const alignSegmentAngle = (id1: string, id2: string, targetAngle: number, mod: number) => {
    const a = get(id1);
    const b = get(id2);
    if (!a || !b) return;
    const cur = Math.atan2(b.y - a.y, b.x - a.x);
    let delta = targetAngle - cur;
    while (delta > mod / 2) delta -= mod;
    while (delta < -mod / 2) delta += mod;
    if (Math.abs(delta) < EPS) return;
    const m1 = movable(id1);
    const m2 = movable(id2);
    if (m1 && m2) {
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      rotateAround(id1, mid, delta);
      rotateAround(id2, mid, delta);
    } else if (m2) {
      rotateAround(id2, a, delta);
    } else if (m1) {
      rotateAround(id1, b, delta);
    }
  };

  /** Ramène la distance |id1 id2| à `target` en glissant les extrémités mobiles. */
  const enforceDistance = (id1: string, id2: string, target: number) => {
    const a = get(id1);
    const b = get(id2);
    if (!a || !b || target < 0) return;
    let dx = b.x - a.x;
    let dy = b.y - a.y;
    let d = Math.hypot(dx, dy);
    if (d < EPS) {
      dx = 1;
      dy = 0;
      d = 1;
    }
    const ux = dx / d;
    const uy = dy / d;
    const diff = target - d;
    const [wa, wb] = weights(id1, id2);
    move(id1, -ux * diff * wa, -uy * diff * wa);
    move(id2, ux * diff * wb, uy * diff * wb);
  };

  const angleOf = (deg: number) => (deg * Math.PI) / 180;

  /**
   * Colinéarité par AJUSTEMENT DE DROITE (moindres carrés / PCA) : on rapproche
   * TOUS les points mobiles d'une droite commune (pas seulement les milieux d'une
   * droite premier↔dernier). Symétrique → bien plus robuste à la convergence sur
   * les systèmes couplés. `ordered` conserve l'ordre/le sens listé (premier→dernier).
   */
  const enforceCollinear = (ids: string[], ordered: boolean) => {
    const pts = ids.map((id) => ({ id, p: get(id) })).filter((o) => o.p) as {
      id: string;
      p: Vec;
    }[];
    if (pts.length < 2) return;
    // Ancre : centroïde des points FIXES si présents (la droite passe par eux),
    // sinon centroïde de tous.
    const fixedPts = pts.filter((o) => !movable(o.id));
    const anchorSet = fixedPts.length ? fixedPts : pts;
    let ax = 0;
    let ay = 0;
    for (const o of anchorSet) {
      ax += o.p.x;
      ay += o.p.y;
    }
    ax /= anchorSet.length;
    ay /= anchorSet.length;
    // Direction principale (PCA) autour de l'ancre.
    let sxx = 0;
    let sxy = 0;
    let syy = 0;
    for (const o of pts) {
      const ddx = o.p.x - ax;
      const ddy = o.p.y - ay;
      sxx += ddx * ddx;
      sxy += ddx * ddy;
      syy += ddy * ddy;
    }
    let ux = 1;
    let uy = 0;
    if (sxx + syy >= EPS) {
      const theta = 0.5 * Math.atan2(2 * sxy, sxx - syy);
      ux = Math.cos(theta);
      uy = Math.sin(theta);
    }
    // Oriente la droite dans le sens premier → dernier (pour l'ordre).
    const fp = pts[0]!.p;
    const lp = pts[pts.length - 1]!.p;
    if ((lp.x - fp.x) * ux + (lp.y - fp.y) * uy < 0) {
      ux = -ux;
      uy = -uy;
    }
    const tOf = (p: Vec) => (p.x - ax) * ux + (p.y - ay) * uy;
    const place = (o: { id: string; p: Vec }, t: number) => {
      if (!movable(o.id)) return;
      o.p.x = ax + t * ux;
      o.p.y = ay + t * uy;
    };

    // Extrémités : projetées sur la droite (elles peuvent glisser) ; elles
    // définissent les bornes du segment.
    const first = pts[0]!;
    const last = pts[pts.length - 1]!;
    const t0 = tOf(first.p);
    const tn = tOf(last.p);
    place(first, t0);
    place(last, tn);
    const lo = Math.min(t0, tn);
    const hi = Math.max(t0, tn);

    // Points intermédiaires : sur la droite, et (si `ordered`) ENTRE les
    // extrémités et après le précédent → ordre/sens tenus, pas de saut.
    let prev = lo;
    for (let k = 1; k < pts.length - 1; k++) {
      const o = pts[k]!;
      const raw = tOf(o.p);
      if (ordered) {
        const t = Math.min(Math.max(raw, prev), hi);
        if (movable(o.id)) {
          place(o, t);
          prev = t;
        } else {
          prev = Math.max(prev, Math.min(raw, hi));
        }
      } else {
        place(o, raw);
      }
    }
  };

  for (let iter = 0; iter < iterations; iter++) {
    for (const c of active) {
      switch (c.type) {
        case 'distance': {
          enforceDistance(c.a, c.b, evalCGValue(c.value, x));
          break;
        }
        case 'equal_length': {
          const a = get(c.a);
          const b = get(c.b);
          if (!a || !b) break;
          enforceDistance(c.c, c.d, Math.hypot(b.x - a.x, b.y - a.y));
          break;
        }
        case 'on_circle': {
          const center = get(c.center);
          const p = get(c.point);
          if (!center || !p || !movable(c.point)) break;
          const r = evalCGValue(c.radius, x);
          let vx = p.x - center.x;
          let vy = p.y - center.y;
          let d = Math.hypot(vx, vy);
          if (d < EPS) {
            vx = 1;
            vy = 0;
            d = 1;
          }
          p.x = center.x + (vx / d) * r;
          p.y = center.y + (vy / d) * r;
          break;
        }
        case 'midpoint': {
          const a = get(c.a);
          const b = get(c.b);
          const p = get(c.point);
          if (!a || !b || !p || !movable(c.point)) break;
          p.x = (a.x + b.x) / 2;
          p.y = (a.y + b.y) / 2;
          break;
        }
        case 'on_function': {
          // Le point glisse sur la courbe : on impose p.y = f(p.x) (abscisse libre).
          const p = get(c.point);
          if (!p || !movable(c.point)) break;
          const y = evalCGValue(c.expr, { ...baseScope, x: p.x });
          if (Number.isFinite(y)) p.y = y;
          break;
        }
        case 'on_line': {
          projectOntoLine(get(c.a), get(c.b), get(c.point), movable(c.point));
          break;
        }
        case 'collinear': {
          if (c.points.length < 3) break;
          enforceCollinear(c.points, c.ordered !== false);
          break;
        }
        case 'parallel': {
          const a = get(c.a);
          const b = get(c.b);
          if (!a || !b) break;
          alignSegmentAngle(c.c, c.d, Math.atan2(b.y - a.y, b.x - a.x), Math.PI);
          break;
        }
        case 'perpendicular': {
          const a = get(c.a);
          const b = get(c.b);
          if (!a || !b) break;
          alignSegmentAngle(c.c, c.d, Math.atan2(b.y - a.y, b.x - a.x) + Math.PI / 2, Math.PI);
          break;
        }
        case 'orientation': {
          // a→b colinéaire au vecteur fixe (vx, vy) : aligne la direction (mod π,
          // les deux sens acceptés), sans toucher à la longueur. Vecteur ~nul ignoré.
          const vx = evalCGValue(c.vx, x);
          const vy = evalCGValue(c.vy, x);
          if (Math.hypot(vx, vy) < EPS) break;
          alignSegmentAngle(c.a, c.b, Math.atan2(vy, vx), Math.PI);
          break;
        }
        case 'angle': {
          const vertex = get(c.vertex);
          const a = get(c.a);
          const b = get(c.b);
          if (!vertex || !a || !b) break;
          const angA = Math.atan2(a.y - vertex.y, a.x - vertex.x);
          const angB = Math.atan2(b.y - vertex.y, b.x - vertex.x);
          let current = angB - angA;
          while (current > Math.PI) current -= 2 * Math.PI;
          while (current < -Math.PI) current += 2 * Math.PI;
          const sign = current >= 0 ? 1 : -1;
          const targetSigned = sign * angleOf(evalCGValue(c.value, x));
          const err = targetSigned - current;
          const [wa, wb] = weights(c.a, c.b);
          rotateAround(c.a, vertex, -err * wa);
          rotateAround(c.b, vertex, err * wb);
          break;
        }
      }
    }
  }

  // Filet de sécurité : borne les coordonnées et neutralise les NaN/Inf
  // (sur-contrainte dégénérée, division par ~0) — jamais de divergence propagée.
  for (const [id, v] of pos) {
    const src = byId.get(id);
    v.x = clampCoord(v.x, src ? src.x : 0);
    v.y = clampCoord(v.y, src ? src.y : 0);
  }

  return { pos, byId, iterations };
}

/**
 * Résout les positions des points sous contraintes, en évaluant les `value`
 * exprimées dans la portée `x` (un nombre = valeur de la variable `x` ;
 * rétro-compat — ou une portée multi-variables `{ a, b, … }`).
 * Ne modifie pas l'entrée ; renvoie un nouveau tableau de points.
 *
 * Utiliser `solveDetailed` pour récupérer les résidus / le statut.
 */
export function solve(
  points: CGPoint[],
  constraints: CGConstraint[],
  x: number | Scope,
  options: SolveOptions = {},
): CGPoint[] {
  const { pos } = relax(points, constraints, x, options);
  return points.map((p) => {
    const v = pos.get(p.id)!;
    return { ...p, x: v.x, y: v.y };
  });
}

/**
 * Variante riche : résout puis calcule l'erreur résiduelle de chaque contrainte.
 * Sert à l'UX prof (contrainte en conflit → rouge), à la détection de
 * non-convergence, et au warm-start de la frame suivante (`positions`).
 */
export function solveDetailed(
  points: CGPoint[],
  constraints: CGConstraint[],
  x: number | Scope,
  options: SolveOptions = {},
): SolveResult {
  const { pos, iterations } = relax(points, constraints, x, options);
  const resolved = points.map((p) => {
    const v = pos.get(p.id)!;
    return { ...p, x: v.x, y: v.y };
  });

  const residualByConstraint: Record<string, number> = {};
  let maxRatio = 0;
  // Les contraintes désactivées n'entrent pas dans le diagnostic de conflit/résidu.
  for (const c of constraints.filter((c) => c.enabled !== false)) {
    const { value, tol } = constraintResidual(c, pos, x);
    residualByConstraint[c.id] = value;
    if (Number.isFinite(value)) maxRatio = Math.max(maxRatio, value / tol);
  }

  return {
    points: resolved,
    positions: pos,
    residualByConstraint,
    residualNorm: maxRatio,
    status: maxRatio <= 1 ? 'converged' : 'conflict',
    iterations,
  };
}

/** Erreur résiduelle d'une contrainte à l'état `pos` (+ sa tolérance de violation). */
function constraintResidual(
  c: CGConstraint,
  pos: Map<string, Vec>,
  x: number | Scope,
): { value: number; tol: number } {
  const g = (id: string) => pos.get(id) ?? null;
  const dist = (a: Vec, b: Vec) => Math.hypot(a.x - b.x, a.y - b.y);
  /** Distance d'un point à la droite (a,b) ; 0 si la droite est dégénérée. */
  const distToLine = (a: Vec, b: Vec, p: Vec): number => {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy);
    if (len < EPS) return 0;
    return Math.abs((p.x - a.x) * dy - (p.y - a.y) * dx) / len;
  };
  /** Écart angulaire (radians) entre deux directions, modulo `mod`, dans [0, mod/2]. */
  const angleGap = (dirA: number, dirB: number, mod: number): number => {
    let d = dirA - dirB;
    while (d > mod / 2) d -= mod;
    while (d < -mod / 2) d += mod;
    return Math.abs(d);
  };
  const dirOf = (a: Vec, b: Vec) => Math.atan2(b.y - a.y, b.x - a.x);

  switch (c.type) {
    case 'distance': {
      const a = g(c.a);
      const b = g(c.b);
      if (!a || !b) return { value: NaN, tol: TOL_LEN };
      return { value: Math.abs(dist(a, b) - evalCGValue(c.value, x)), tol: TOL_LEN };
    }
    case 'equal_length': {
      const a = g(c.a);
      const b = g(c.b);
      const cc = g(c.c);
      const d = g(c.d);
      if (!a || !b || !cc || !d) return { value: NaN, tol: TOL_LEN };
      return { value: Math.abs(dist(a, b) - dist(cc, d)), tol: TOL_LEN };
    }
    case 'on_circle': {
      const center = g(c.center);
      const p = g(c.point);
      if (!center || !p) return { value: NaN, tol: TOL_LEN };
      return { value: Math.abs(dist(center, p) - evalCGValue(c.radius, x)), tol: TOL_LEN };
    }
    case 'midpoint': {
      const a = g(c.a);
      const b = g(c.b);
      const p = g(c.point);
      if (!a || !b || !p) return { value: NaN, tol: TOL_LEN };
      return { value: dist(p, { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }), tol: TOL_LEN };
    }
    case 'on_function': {
      const p = g(c.point);
      if (!p) return { value: NaN, tol: TOL_LEN };
      const base = typeof x === 'number' ? { x } : x;
      const y = evalCGValue(c.expr, { ...base, x: p.x });
      if (!Number.isFinite(y)) return { value: NaN, tol: TOL_LEN };
      return { value: Math.abs(p.y - y), tol: TOL_LEN };
    }
    case 'on_line': {
      const a = g(c.a);
      const b = g(c.b);
      const p = g(c.point);
      if (!a || !b || !p) return { value: NaN, tol: TOL_LEN };
      return { value: distToLine(a, b, p), tol: TOL_LEN };
    }
    case 'collinear': {
      if (c.points.length < 3) return { value: 0, tol: TOL_LEN };
      const first = g(c.points[0]!);
      const last = g(c.points[c.points.length - 1]!);
      if (!first || !last) return { value: NaN, tol: TOL_LEN };
      let worst = 0;
      for (let k = 1; k < c.points.length - 1; k++) {
        const p = g(c.points[k]!);
        if (p) worst = Math.max(worst, distToLine(first, last, p));
      }
      return { value: worst, tol: TOL_LEN };
    }
    case 'parallel': {
      const a = g(c.a);
      const b = g(c.b);
      const cc = g(c.c);
      const d = g(c.d);
      if (!a || !b || !cc || !d) return { value: NaN, tol: TOL_ANG };
      return { value: angleGap(dirOf(a, b), dirOf(cc, d), Math.PI), tol: TOL_ANG };
    }
    case 'perpendicular': {
      const a = g(c.a);
      const b = g(c.b);
      const cc = g(c.c);
      const d = g(c.d);
      if (!a || !b || !cc || !d) return { value: NaN, tol: TOL_ANG };
      return {
        value: angleGap(dirOf(a, b) + Math.PI / 2, dirOf(cc, d), Math.PI),
        tol: TOL_ANG,
      };
    }
    case 'angle': {
      const vertex = g(c.vertex);
      const a = g(c.a);
      const b = g(c.b);
      if (!vertex || !a || !b) return { value: NaN, tol: TOL_ANG };
      const current = angleGap(dirOf(vertex, b), dirOf(vertex, a), 2 * Math.PI);
      const target = Math.abs((evalCGValue(c.value, x) * Math.PI) / 180);
      return { value: Math.abs(current - target), tol: TOL_ANG };
    }
    case 'orientation': {
      const a = g(c.a);
      const b = g(c.b);
      if (!a || !b) return { value: NaN, tol: TOL_ANG };
      const vx = evalCGValue(c.vx, x);
      const vy = evalCGValue(c.vy, x);
      if (Math.hypot(vx, vy) < EPS) return { value: 0, tol: TOL_ANG };
      return { value: angleGap(dirOf(a, b), Math.atan2(vy, vx), Math.PI), tol: TOL_ANG };
    }
  }
}

/** Projette `p` sur la droite (a,b) si `canMove`. Mutation en place de `p`. */
function projectOntoLine(a: Vec | null, b: Vec | null, p: Vec | null, canMove: boolean) {
  if (!a || !b || !p || !canMove) return;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 < EPS) return;
  const t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
  p.x = a.x + t * dx;
  p.y = a.y + t * dy;
}
